import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { History, ChevronDown } from "lucide-react";
import { formatPhone } from "@/lib/utils";
import { ACTION_LABELS, actionLabel } from "@/components/ActivitySection";

const FIELD_LABELS = {
  first_name: "First name",
  last_name: "Last name",
  phone: "Phone",
  email: "Email",
  address: "Address",
  address_verified: "Address verified",
  verification_note: "Verification note",
  gender: "Gender",
  birth_year: "Birth year",
  notes: "Notes",
  relationship: "Relationship",
  eligible: "Eligible",
  eligibility_notes: "Eligibility notes",
};

function fmt(val) {
  if (val === null || val === undefined || val === "") return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  return String(val);
}

function fmtField(field, val) {
  if (field === "phone" && val) return formatPhone(val);
  return fmt(val);
}

function memberKey(m) {
  return [m.first_name, m.last_name, m.relationship, m.birth_year]
    .map((v) => (v ?? "")).join("|");
}

function memberLabel(m) {
  const name = [m.first_name, m.last_name].filter(Boolean).join(" ") || "—";
  const rel = m.relationship ? ` (${m.relationship})` : "";
  const age = m.birth_year ? `, b. ${m.birth_year}` : "";
  return `${name}${rel}${age}`;
}

function MemberDiff({ previousMembers, submittedMembers }) {
  const prevKeys = new Map((previousMembers || []).map((m) => [memberKey(m), m]));
  const subKeys = new Map((submittedMembers || []).map((m) => [memberKey(m), m]));

  const added = (submittedMembers || []).filter((m) => !prevKeys.has(memberKey(m)));
  const removed = (previousMembers || []).filter((m) => !subKeys.has(memberKey(m)));
  const unchanged = (previousMembers || []).filter((m) => subKeys.has(memberKey(m)));

  if (added.length === 0 && removed.length === 0 && unchanged.length === 0) return null;

  return (
    <div>
      <div
        className="px-4 py-2 text-[10px] font-semibold uppercase tracking-wider"
        style={{ background: "var(--fb-bg)", color: "var(--fb-text-muted)", borderBottom: "1px solid var(--fb-border)" }}
      >
        Household members
      </div>
      {removed.map((m, i) => (
        <div
          key={`r-${i}`}
          className="flex items-center gap-2 px-4 py-2 text-xs border-t font-mono"
          style={{ borderColor: "var(--fb-border)", color: "#b91c1c", background: "#fff1f2" }}
        >
          <span className="font-bold select-none">−</span>
          <span style={{ textDecoration: "line-through" }}>{memberLabel(m)}</span>
        </div>
      ))}
      {added.map((m, i) => (
        <div
          key={`a-${i}`}
          className="flex items-center gap-2 px-4 py-2 text-xs border-t font-mono"
          style={{ borderColor: "var(--fb-border)", color: "#15803d", background: "#f0fdf4" }}
        >
          <span className="font-bold select-none">+</span>
          <span>{memberLabel(m)}</span>
        </div>
      ))}
      {unchanged.map((m, i) => (
        <div
          key={`u-${i}`}
          className="flex items-center gap-2 px-4 py-2 text-xs border-t font-mono"
          style={{ borderColor: "var(--fb-border)", color: "var(--fb-text-muted)" }}
        >
          <span className="font-bold select-none opacity-0">·</span>
          <span>{memberLabel(m)}</span>
        </div>
      ))}
    </div>
  );
}

function ApproveUpdateDiff({ submitted, applied, submittedMembers, previousMembers }) {
  const fields = Object.keys(submitted || {}).filter((k) => k !== "updated_at");
  const hasEdits = !!applied;
  const hasMemberChanges = submittedMembers !== undefined || previousMembers !== undefined;

  return (
    <div className="space-y-3">
      {hasEdits && (
        <div
          className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium"
          style={{ background: "#fef9c3", color: "#854d0e", border: "1px solid #fde047" }}
        >
          Staff applied edits before approving
        </div>
      )}
      {fields.length > 0 && (
        <div className="rounded-lg overflow-hidden text-xs" style={{ border: "1px solid var(--fb-border)" }}>
          <table className="w-full">
            <thead>
              <tr style={{ background: "var(--fb-bg)" }}>
                <th
                  className="text-left px-4 py-2 font-semibold uppercase tracking-wider text-[10px]"
                  style={{ color: "var(--fb-text-muted)", width: "28%" }}
                >
                  Field
                </th>
                <th
                  className="text-left px-4 py-2 font-semibold uppercase tracking-wider text-[10px]"
                  style={{ color: "var(--fb-text-muted)" }}
                >
                  Client submitted
                </th>
                {hasEdits && (
                  <th
                    className="text-left px-4 py-2 font-semibold uppercase tracking-wider text-[10px]"
                    style={{ color: "var(--fb-text-muted)" }}
                  >
                    Staff applied
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {fields.map((field, i) => {
                const submittedVal = submitted[field];
                const appliedVal = hasEdits ? applied[field] : undefined;
                const wasEdited = hasEdits && fmtField(field, submittedVal) !== fmtField(field, appliedVal);
                return (
                  <tr key={i} className="border-t" style={{ borderColor: "var(--fb-border)" }}>
                    <td className="px-4 py-2 font-medium" style={{ color: "var(--fb-text)" }}>
                      {FIELD_LABELS[field] ?? field}
                    </td>
                    <td
                      className="px-4 py-2 font-mono"
                      style={{
                        color: wasEdited ? "#b91c1c" : "var(--fb-text)",
                        textDecoration: wasEdited ? "line-through" : "none",
                      }}
                    >
                      {fmtField(field, submittedVal)}
                    </td>
                    {hasEdits && (
                      <td
                        className="px-4 py-2 font-mono"
                        style={{ color: wasEdited ? "#15803d" : "var(--fb-text-muted)" }}
                      >
                        {wasEdited ? fmtField(field, appliedVal) : "—"}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {hasMemberChanges && (
        <div className="rounded-lg overflow-hidden text-xs" style={{ border: "1px solid var(--fb-border)" }}>
          <MemberDiff previousMembers={previousMembers} submittedMembers={submittedMembers} />
        </div>
      )}
    </div>
  );
}

function DeletedVisitDetail({ details }) {
  const bags = (details?.bags || []).map((bag) => bag.name).filter(Boolean);
  const rows = [
    ["Visit date", details?.visit_date ? String(details.visit_date).slice(0, 10) : "—"],
    ["Recorded by", details?.recorded_by || "—"],
    ["Bags", bags.length ? bags.join(" + ") : "None recorded"],
    ["Weight", Number(details?.total_weight_lbs) > 0 ? `${details.total_weight_lbs} lbs` : "—"],
    ["Notes", details?.notes || "—"],
  ];
  return (
    <div className="rounded-lg overflow-hidden text-xs" style={{ border: "1px solid var(--fb-border)" }}>
      {rows.map(([label, value], index) => (
        <div key={label} className="grid grid-cols-[9rem_1fr] border-t first:border-t-0" style={{ borderColor: "var(--fb-border)" }}>
          <div className="px-4 py-2 font-semibold" style={{ background: "var(--fb-bg)" }}>{label}</div>
          <div className="px-4 py-2">{value}</div>
        </div>
      ))}
    </div>
  );
}

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/audit-logs");
      setLogs(data);
    })();
  }, []);

  function toggleExpand(id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: "#8B918B" }}>Activity</div>
        <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>Audit Log</h1>
        <p className="text-sm mt-2" style={{ color: "var(--fb-text-muted)" }}>All staff actions are logged.</p>
      </div>

      <div className="fb-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr style={{ background: "var(--fb-bg)" }}>
                <th className="text-left px-6 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>When</th>
                <th className="text-left px-6 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Who · What</th>
                <th className="text-left px-6 py-3 font-semibold uppercase tracking-wider text-xs" style={{ color: "var(--fb-text-muted)" }}>Target</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-sm" style={{ color: "var(--fb-text-muted)" }}>
                    <History className="w-8 h-8 mx-auto mb-2" />
                    No actions logged yet.
                  </td>
                </tr>
              )}
              {logs.map((l) => {
                const changes = l.details?.changes;
                const hasChanges = Array.isArray(changes) && changes.length > 0;
                const submittedValues = l.details?.submitted_values;
                const appliedValues = l.details?.applied_values;
                const submittedMembers = l.details?.submitted_members;
                const previousMembers = l.details?.previous_members;
                const hasContactFields = !!submittedValues && Object.keys(submittedValues).filter((k) => k !== "updated_at").length > 0;
                const hasMemberDetail = submittedMembers !== undefined || previousMembers !== undefined;
                const hasApproveDetail = l.action === "approve_update" && (hasContactFields || hasMemberDetail);
                const hasDeletedVisitDetail = l.action === "delete_visit" && !!l.details;
                const isExpandable = hasChanges || hasApproveDetail || hasDeletedVisitDetail;
                const isOpen = expanded.has(l.id);
                return (
                  <React.Fragment key={l.id}>
                    <tr className="border-t" style={{ borderColor: "var(--fb-border)" }}>
                      <td className="px-6 py-3 text-xs tabular-nums" style={{ color: "var(--fb-text-muted)" }}>
                        {new Date(l.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-3">
                        <span className="font-medium">{l.actor}</span>
                        <span className="mx-1.5 text-xs" style={{ color: "var(--fb-text-muted)" }}>·</span>
                        <span title={l.action}>{actionLabel(l.action)}</span>
                      </td>
                      <td className="px-6 py-3 text-xs" style={{ color: "var(--fb-text-muted)" }}>{l.target_type}#{l.target_id?.slice(-6) || ""}</td>
                      <td className="px-6 py-3 text-right">
                        {isExpandable && (
                          <button
                            data-testid={`expand-diff-${l.id}`}
                            onClick={() => toggleExpand(l.id)}
                            className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full hover:bg-stone-100 transition-colors"
                            style={{ color: "var(--fb-primary)" }}
                          >
                            {hasApproveDetail
                              ? (appliedValues ? "Edited" : hasMemberDetail && !hasContactFields ? "Members" : "Submitted")
                              : hasDeletedVisitDetail ? "Visit details"
                              : `${changes.length} field${changes.length !== 1 ? "s" : ""}`}
                            <ChevronDown
                              className="w-3.5 h-3.5 transition-transform"
                              style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                            />
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpandable && isOpen && (
                      <tr style={{ borderColor: "var(--fb-border)" }}>
                        <td colSpan={4} className="px-6 pb-4 pt-0">
                          {hasApproveDetail ? (
                            <ApproveUpdateDiff
                              submitted={submittedValues}
                              applied={appliedValues}
                              submittedMembers={submittedMembers}
                              previousMembers={previousMembers}
                            />
                          ) : hasDeletedVisitDetail ? (
                            <DeletedVisitDetail details={l.details} />
                          ) : (
                            <div
                              className="rounded-lg overflow-hidden text-xs"
                              style={{ border: "1px solid var(--fb-border)" }}
                            >
                              <table className="w-full">
                                <thead>
                                  <tr style={{ background: "var(--fb-bg)" }}>
                                    <th
                                      className="text-left px-4 py-2 font-semibold uppercase tracking-wider text-[10px]"
                                      style={{ color: "var(--fb-text-muted)", width: "28%" }}
                                    >
                                      Field
                                    </th>
                                    <th
                                      className="text-left px-4 py-2 font-semibold uppercase tracking-wider text-[10px]"
                                      style={{ color: "var(--fb-text-muted)" }}
                                    >
                                      Before
                                    </th>
                                    <th
                                      className="text-left px-4 py-2 font-semibold uppercase tracking-wider text-[10px]"
                                      style={{ color: "var(--fb-text-muted)" }}
                                    >
                                      After
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {changes.map((ch, i) => (
                                    <tr
                                      key={i}
                                      className="border-t"
                                      style={{ borderColor: "var(--fb-border)" }}
                                    >
                                      <td
                                        className="px-4 py-2 font-medium"
                                        style={{ color: "var(--fb-text)" }}
                                      >
                                        {FIELD_LABELS[ch.field] ?? ch.field}
                                      </td>
                                      <td
                                        className="px-4 py-2 font-mono"
                                        style={{
                                          color:
                                            ch.old_value == null || ch.old_value === ""
                                              ? "var(--fb-text-muted)"
                                              : "#b91c1c",
                                        }}
                                      >
                                        {fmtField(ch.field, ch.old_value)}
                                      </td>
                                      <td
                                        className="px-4 py-2 font-mono"
                                        style={{ color: "#15803d" }}
                                      >
                                        {fmtField(ch.field, ch.new_value)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
